import { DraftingCompass, PackageSearch, Boxes, Forklift, ClipboardCheck, Power, KanbanSquare, GanttChart, HardHat, ShieldCheck, ArrowRight } from "lucide-react";
import { Reveal, SectionHead, PageHero, CTASection, usePageMeta } from "../components/ui-bits";
import { IMG, services, epcFlow } from "../data/content";

const icons = { DraftingCompass, PackageSearch, Boxes, Crane: Forklift, ClipboardCheck, Power, KanbanSquare, GanttChart, HardHat, ShieldCheck };

const Services = () => {
  usePageMeta(
    "Services — EPC / LSTK, Design, Procurement, Erection & Commissioning | G. R. Engineering",
    "Residual & detailed design, procurement, material management, installation & erection, pre-commissioning, commissioning and total project management for process plants on EPC / LSTK basis."
  );
  return (
    <>
      <PageHero
        img={IMG("hmel-erection")}
        eyebrow="Services"
        title="From residual design to commissioned plant"
        sub="Complete project management for the construction of process plants — oil & gas refineries, petrochemicals, fertilizer and nuclear power — on EPC / LSTK basis."
      />

      <section className="bg-white" data-testid="services-grid">
        <div className="container-gre py-20 lg:py-28">
          <SectionHead eyebrow="What We Do" title="Ten disciplines. One accountable team." />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {services.map((s, i) => {
              const Icon = icons[s.icon];
              return (
                <Reveal key={s.title} delay={(i % 3) * 0.06}>
                  <div className="group bg-white p-8 h-full card-lift border border-transparent" data-testid={`service-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex items-center justify-between">
                      <Icon size={26} className="text-[#C5221F]" strokeWidth={1.6} />
                      <span className="font-mono-tech text-xs font-bold text-slate-200 group-hover:text-[#C5221F] transition-colors duration-300">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{s.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#0A1128] relative overflow-hidden" data-testid="epc-flow">
        <div className="absolute inset-0 blueprint-grid" />
        <div className="container-gre relative py-20 lg:py-28">
          <SectionHead light eyebrow="EPC / LSTK Workflow" title="How a project moves through G. R. Engineering" sub="A single accountable chain from engineering to handover — no hand-offs lost between contractors." />
          <div className="mt-14 flex flex-wrap gap-3">
            {epcFlow.map((step, i) => (
              <Reveal key={step} delay={i * 0.05}>
                <div className="flex items-center gap-3">
                  <div className="border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-3.5 hover:border-[#C5221F] transition-colors duration-300">
                    <span className="font-mono-tech text-[10px] font-bold text-red-400 block">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display text-sm font-semibold text-white">{step}</span>
                  </div>
                  {i < epcFlow.length - 1 && <ArrowRight size={14} className="text-slate-600 hidden xl:block" />}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-b border-slate-200" data-testid="services-proof">
        <div className="container-gre py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMG("jetty-barge")} alt="Oversized equipment loaded on barge at captive jetty" className="w-full aspect-[16/9] object-cover img-industrial border border-slate-200" />
          </Reveal>
          <div>
            <SectionHead
              eyebrow="In Practice"
              title="Executed at mega-project scale"
              sub="Complete EPC of the C4 Hydrogenation Unit at OPaL Dahej — from soil investigation and detailed engineering to civil works, modularized structures, commissioning, PGTR and operational assistance. Site fabrication of 2,230 MT towers at HMEL Bathinda. Cross-border RO-RO delivery to Mongla, Bangladesh."
            />
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Services;
